// §11e — on-Meta vs website leads, on BOTH axes, side by side.
//
// ⚠️⚠️ TWO AXES, LABELLED, DELIBERATELY NOT RECONCILED. §11d's columns split by
// lead ACTION; this panel shows that axis AND the campaign-SETUP one, because
// they answer different questions and a specialist wants both ("which mechanism
// is converting" and "which campaign type is carrying it"). They correlate and
// are NOT the same thing — a website-lead campaign can still record an on-Meta
// lead action — so each carries its own heading and its own one-line note, and
// neither is presented as a check on the other.
//
// ⚠️⚠️ EACH AXIS SHOWS ITS OWN RESIDUAL. Two bars that silently fail to sum to
// the total above them is the trap §11e names — and the residuals are DIFFERENT
// things on the two axes (attribution falling outside both buckets, versus
// campaigns that are not lead campaigns at all), so each is labelled in its own
// words rather than sharing a generic "Other".
//
// ⚠️ LEADS ARE A COUNT, so a proportion bar over them is honest. MONEY IS NOT
// BLENDED: spend renders per currency beside the bar, never summed across.

import {
  campaignSetupAxis,
  leadActionAxis,
  slicePct,
  type LeadAxis,
  type LeadAxisSlice,
} from '../../lib/advertisingHome/leadAxes';
import type { MetaLeadBreakdown } from '../../types/advertisingHome';
import type { FxRateSnapshot } from '../../types/fxRates';
import { PanelTitle } from '../reporting/PanelTitle';
import { formatLeads, homeMoney } from './homeMoney';

/** A residual is muted, so it never competes with the two named slices. */
const SLICE_FILL: Record<string, string> = {
  meta: 'bg-kyck-500',
  on_meta_lead: 'bg-kyck-500',
  website: 'bg-blue-500',
  website_lead: 'bg-blue-500',
  other: 'bg-slate-300',
};

function SliceRow({
  slice,
  axis,
  display,
  snapshot,
}: {
  slice: LeadAxisSlice;
  axis: LeadAxis;
  display: string;
  snapshot: FxRateSnapshot | null | undefined;
}): React.JSX.Element {
  const pct = slicePct(slice, axis);
  return (
    <div className="space-y-0.5">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className={slice.residual ? 'text-muted-foreground' : 'font-medium'}>
          {slice.label}
        </span>
        <span className="shrink-0 tabular-nums text-muted-foreground">
          {formatLeads(slice.leads)} leads
          {/* ⚠️ A null share renders a dash, not 0% — an axis with nothing
              measured has no shares, and a row of 0% bars reads as "we measured
              this and it was nothing". */}
          {pct === null ? ' · —' : ` · ${pct}%`}
          {slice.spend.length > 0 && <> · {homeMoney(slice.spend, display, snapshot)}</>}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded bg-muted">
        <div
          className={`h-full ${SLICE_FILL[slice.key] ?? 'bg-slate-300'}`}
          style={{ width: `${pct ?? 0}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}

function Axis({
  axis,
  display,
  snapshot,
  footnote,
}: {
  axis: LeadAxis;
  display: string;
  snapshot: FxRateSnapshot | null | undefined;
  footnote?: string;
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      <div>
        <p className="text-xs font-semibold">{axis.title}</p>
        {/* ⚠️ THE NOTE LEADS THE FIGURES. A caveat read after a number has been
            believed is not a caveat. */}
        <p className="text-[11px] text-muted-foreground">{axis.note}</p>
      </div>
      <div className="space-y-2">
        {axis.slices.map((s) => (
          <SliceRow key={s.key} slice={s} axis={axis} display={display} snapshot={snapshot} />
        ))}
      </div>
      <p className="text-[11px] tabular-nums text-muted-foreground">
        {formatLeads(axis.totalLeads)} leads in total
      </p>
      {footnote && <p className="text-[11px] text-amber-700">{footnote}</p>}
    </div>
  );
}

export function LeadSplitPanel({
  rows,
  display,
  snapshot,
}: {
  rows: readonly MetaLeadBreakdown[];
  display: string;
  snapshot: FxRateSnapshot | null | undefined;
}): React.JSX.Element {
  const action = leadActionAxis(rows);
  const setup = campaignSetupAxis(rows);
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <PanelTitle>Where the leads came from</PanelTitle>
        <span className="text-[10px] text-muted-foreground">
          the accounts and range selected above
        </span>
      </div>
      {/* ⚠️⚠️ THE SENTENCE THAT STOPS THE RECONCILIATION. Two splits of the same
          leads, side by side, invite a reader to check one against the other —
          and they legitimately differ. Saying so is the point of showing both. */}
      <p className="mb-3 text-[11px] text-muted-foreground">
        Two different splits of the same leads.{' '}
        <strong className="font-medium">They will not agree, and that is correct</strong> — a
        website-lead campaign can still record an on-Meta lead, and the other way round.
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No Meta figures in this range.</p>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <Axis
            axis={action}
            display={display}
            snapshot={snapshot}
            footnote={
              action.residualUnderflow
                ? 'The two named types add up to more than the grouped total for this selection, so the remainder is shown as zero rather than as a negative.'
                : undefined
            }
          />
          <Axis axis={setup} display={display} snapshot={snapshot} />
        </div>
      )}
    </div>
  );
}
